import { call, put } from 'redux-saga/effects';

import { Messages } from '../../../../codegen/Localize';
import { Metrics } from '../../../../metrics/resources/Metrics';
import { PublishedConnections } from '../../../../publishedConnection/resources/PublishedConnections';
import { assertUnreachable } from '../../../../ts/helpers/TypescriptHelpers';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { Databases } from '../../../../catalog/resources/Databases';
import { Tables } from '../../../../catalog/resources/Tables';
import { IResult } from '../../../../httpClient/serverApi/types/IResponse';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { SetDescriptionAction } from '../../../actions/spaces/SetDescriptionAction';
import {
  DESCRIPTION_MAXIMUM_LENGTH,
  DescriptionDialogState,
  METRIC_DESCRIPTION_MAXIMUM_LENGTH,
  getDescriptionDialogOptions,
} from '../../../content/DescriptionDialog';
import { reloadSpaceIfUpdatingCurrentSpace } from './RefreshContentSagas';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { FeatureFlags } from '~src/ts/FeatureFlags';

export function* handleSetDescription(action: SetDescriptionAction) {
  try {
    yield call(openDescriptionDialog, action.item);

    const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.item]);
    if (!maybeReloaded) {
      yield put(requestSpaceContents());
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

function setDescription(
  item: IContentInfo<'database'> | IContentInfo<'table'> | IContentInfo<'metric'> | IContentInfo<'publishedConnection'>,
  state: DescriptionDialogState
): Promise<IResult> {
  switch (item.contentType) {
    case 'database': return Databases.setDatabaseDescription({
      description: state.description,
      id: item.vizportalId,
    });
    case 'table': return Tables.setTableDescription({
      description: state.description,
      id: item.vizportalId,
    });
    case 'metric': return Metrics.setMetricDescription({
      description: state.description,
      id: item.id,
    });
    case 'publishedConnection': return PublishedConnections.setDescription({
      description: state.description,
      id: item.id,
    });
    default: assertUnreachable(item);
  }
}

export function openDescriptionDialog(item: IContentInfo<'database'> | IContentInfo<'table'> | IContentInfo<'metric'> | IContentInfo<'publishedConnection'>): PromiseLike<unknown> {
  let maxCharacterLimit: number;
  let useMarkup = FeatureFlags.markupEditingEnabled;

  switch(item.contentType) {
    case 'database':
    case 'table':
      useMarkup = true;
      maxCharacterLimit = DESCRIPTION_MAXIMUM_LENGTH;
      break;
    case 'publishedConnection':
      maxCharacterLimit = DESCRIPTION_MAXIMUM_LENGTH;
      useMarkup = false;
      break;
    case 'metric':
      maxCharacterLimit = METRIC_DESCRIPTION_MAXIMUM_LENGTH;
      useMarkup = false;
      break;
    default: assertUnreachable(item);
  }

  const modalInstance = openReactActionDialog(getDescriptionDialogOptions({
    initialBodyProps: { useMarkup, maxCharacterLimit },
    initialFormData: { description: item.description || '' },
    doAction: (formData) => setDescription(item, formData)
  }));
  return modalInstance.result;
}
